<template>
  <div class="scenario-manager">
    <!-- Overlay de bloqueio: cobre o modal durante carregar/ativar ON-LINE -->
    <div v-if="loading" class="scenario-blocking-overlay">
      <div class="scenario-blocking-content">
        <b-spinner class="scenario-blocking-spinner" />
        <span class="scenario-blocking-text">{{ loadingMessage || 'Carregando...' }}</span>
      </div>
    </div>
    <div class="scenario-manager-panels">
    <!-- Painel esquerdo: árvore de pastas -->
    <div class="panel-left">
      <div class="toolbar">
        <b-button size="sm" variant="outline-secondary" @click="createFolder" :disabled="loading">
          <b-icon icon="folder-plus" /> Nova Pasta
        </b-button>
        <b-button size="sm" variant="outline-secondary" @click="renameItem" :disabled="!selectedItem || !selectedItem.isFolder || selectedItem.isLocked || loading">
          <b-icon icon="pencil" /> Renomear Pasta
        </b-button>
        <b-button v-if="!selectedItem || !selectedItem.isLocked" size="sm" variant="outline-danger" @click="confirmDelete" :disabled="!selectedItem || !selectedItem.isFolder || loading">
          <b-icon icon="trash" /> Excluir Pasta
        </b-button>
      </div>
      <div class="search-wrapper mb-2">
        <b-icon icon="search" class="search-icon" />
        <input
          v-model="searchText"
          type="text"
          placeholder="Buscar cenário..."
          class="search-input input-pentagro"
        />
        <button v-if="searchText" class="search-clear" @click="searchText = ''" tabindex="-1" title="Limpar busca">
          <b-icon icon="x" />
        </button>
      </div>
      <div class="tree-container">
        <div v-if="loading" class="skeleton-tree">
          <div v-for="row in skeletonRows" :key="row.key" class="skeleton-row" :style="{ paddingLeft: row.indent + 'px' }">
            <span class="skeleton-icon shimmer"></span>
            <span class="skeleton-label shimmer" :style="{ width: row.width + 'px' }"></span>
          </div>
          <div class="skeleton-loading-message">{{ loadingMessage || 'Carregando...' }}</div>
        </div>
        <tree
          v-else
          ref="tree"
          :data="treeData"
          :options="treeOptions"
          :filter="searchText"
          @node:selected="handleNodeSelect"
          @node:dblclick="handleDblClick"
          @node:dragging:finish="handleDrop"
        >
          <span slot-scope="{ node }" class="tree-node-slot">
            <template v-if="node.data.isVirtualRoot">
              <b-icon icon="diagram-3-fill" class="domain-root-icon" />
              <strong class="domain-root-label">{{ node.text }}</strong>
            </template>
            <template v-else>
              <b-icon v-if="node.data.isFolder" icon="folder-fill" class="folder-icon" />
              <b-icon v-else icon="file-earmark-text" class="file-icon" />
              <span class="node-label" :class="{ 'locked-node': node.data.isLocked }">{{ node.text }}</span>
              <b-icon
                v-if="!node.data.isFolder && node.data.isCompatible === false"
                icon="exclamation-circle-fill"
                class="incompatible-icon"
                title="Cenário incompatível com a versão atual"
              />
              <b-icon
                v-if="!node.data.isFolder && node.data.isOutdated"
                icon="exclamation-triangle-fill"
                class="outdated-icon"
                title="Cenário fora do padrão (versão desatualizada ou diagrama diferente do PADRÃO)"
              />
              <b-icon
                v-if="!node.data.isFolder && node.data.scenarioType === 'RTO' && node.data.rtoObjetoXmlId && rtoStatusMap[node.data.rtoObjetoXmlId] === 'Ativo'"
                icon="check-circle-fill"
                class="rto-active-icon"
                title="Ativo no RTO — modelo atualmente em uso pelo Worker"
              />
            </template>
          </span>
        </tree>
      </div>
    </div>

    <!-- Painel direito: editor -->
    <div class="panel-right">
      <transition name="panel-fade" mode="out-in">
      <div :key="selectedItem ? selectedItem.id : '__empty__'" class="panel-right-inner">
      <div class="editor-toolbar">
        <b-button size="sm" variant="outline-secondary" @click="newScenario">
          <b-icon icon="file-earmark" /> Novo Cenário
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="copyScenario"
          :disabled="!selectedItem || selectedItem.isFolder || selectedItem.isCompatible === false || selectedItem.isOutdated || loading"
        >
          <b-icon icon="file-earmark-arrow-up" /> Copiar
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          @click="renameScenario"
          :disabled="!selectedItem || selectedItem.isFolder || selectedItem.isLocked || loading || isSelectedActiveOnline"
          :title="isSelectedActiveOnline ? 'Cenário ativo no ON-LINE — desative o ON-LINE para renomear.' : ''"
        >
          <b-icon icon="pencil" /> Renomear Cenário
        </b-button>
      </div>

      <div v-if="selectedItem && !selectedItem.isFolder && selectedItem.isCompatible === false" class="incompatibility-banner">
        <b-icon icon="exclamation-triangle" /> Este cenário foi gerado em uma versão diferente e pode não ser compatível com a versão atual do sistema.
      </div>

      <transition name="banner-slide">
      <div v-if="xmlDifferences.length > 0" class="xml-diff-banner">
        <b-icon icon="exclamation-circle-fill" class="xml-diff-icon" />
        <div class="xml-diff-items">
          <template v-for="(diff, i) in xmlDifferences">
            <hr v-if="i > 0" :key="'hr-' + i" class="xml-diff-separator" />
            <div :key="i" class="xml-diff-item" :title="`${diff.label} — PADRÃO: ${diff.padrao} — Cenário: ${diff.cenario}`">
              <strong>{{ diff.label }}</strong> &mdash; PADRÃO: {{ diff.padrao }} &mdash; Cenário: {{ diff.cenario }}
            </div>
          </template>
        </div>
      </div>
      </transition>

      <div v-if="!isViewingExisting || formName" class="form-group mt-2">
        <label class="form-label">Nome</label>
        <div v-if="isViewingExisting" class="form-field-text">{{ formName }}</div>
        <b-form-input
          v-else
          v-model="formName"
          placeholder="Nome do cenário"
          class="input-pentagro"
          :state="isNameEmpty ? false : null"
        />
        <small v-if="!isViewingExisting && isNameEmpty" class="name-required-hint">
          <b-icon icon="exclamation-circle-fill" /> Informe um nome — não é possível salvar com o nome vazio.
        </small>
      </div>

      <div v-if="hasRtoPermission" class="form-group mt-2">
        <label class="form-label">Tipo</label>
        <div v-if="isViewingExisting" class="form-field-text">{{ formDomain }}</div>
        <b-form-select v-else v-model="formDomain" class="input-pentagro" @change="formParentId = null">
          <b-form-select-option value="PGDI">PGDI</b-form-select-option>
          <b-form-select-option value="RTO">RTO</b-form-select-option>
        </b-form-select>
      </div>

      <div class="form-group mt-2">
        <label class="form-label">Pasta</label>
        <div v-if="isViewingExisting" class="form-field-text">{{ formParentName }}</div>
        <b-form-select v-else v-model="formParentId" class="input-pentagro">
          <b-form-select-option :value="null">— Raiz —</b-form-select-option>
          <b-form-select-option
            v-for="folder in folderList"
            :key="folder.id"
            :value="folder.id"
          >{{ folder.name }}</b-form-select-option>
        </b-form-select>
      </div>

      <div class="action-buttons mt-3">
        <b-button
          v-if="!selectedItem || !selectedItem.isLocked"
          size="sm"
          variant="outline-danger"
          class="action-btn"
          @click="confirmDeleteScenario"
          :disabled="!selectedItem || selectedItem.isFolder || loading || isSelectedActiveOnline"
          :title="isSelectedActiveOnline ? 'Cenário ativo no ON-LINE — desative o ON-LINE para excluir.' : 'Excluir Cenário'"
        >
          <b-icon icon="trash-fill" /> Excluir
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="action-btn"
          @click="clearForm"
          title="Limpar"
        >
          <b-icon icon="x-circle" /> Limpar
        </b-button>
        <input type="file" ref="xmlFileInput" accept=".xml" hidden @change="handleXmlFile" />
        <b-button v-if="!selectedItem || !selectedItem.isLocked || isPentagroUser" size="sm" variant="outline-secondary" class="action-btn" @click="$refs.xmlFileInput.click()" title="Importar XML">
          <b-icon icon="upload" /> Importar
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="action-btn"
          @click="exportItem"
          :disabled="!selectedItem || selectedItem.isFolder || loading"
          title="Exportar XML"
        >
          <b-icon icon="box-arrow-down" /> Exportar
        </b-button>
        <b-button
          v-if="selectedItem && !selectedItem.isFolder && selectedItem.scenarioType === 'RTO' && selectedItem.rtoObjetoXmlId && hasRtoPermission && !isCurrentScenarioActiveInRto"
          size="sm"
          variant="success"
          class="action-btn"
          @click="activateRtoScenario"
          :disabled="loading"
          title="Definir este XML como modelo ativo no RTO"
        >
          <b-icon icon="check-circle-fill" /> Ativar no RTO
        </b-button>
        <b-button
          size="sm"
          class="action-btn btn-pentagro-load"
          @click="confirmLoad"
          :disabled="!selectedItem || selectedItem.isFolder || selectedItem.isCompatible === false || loading"
          :title="pendingOnlineActivation ? 'Ativar ON-LINE com este cenário' : 'Carregar no PAP'"
        >
          <b-icon :icon="pendingOnlineActivation ? 'play-fill' : 'folder-fill'" />
          {{ pendingOnlineActivation ? 'Ativar ON-LINE' : 'Carregar' }}
        </b-button>
        <b-button
          v-if="!selectedItem || !selectedItem.isLocked || isPentagroUser"
          size="sm"
          class="action-btn btn-pentagro-save"
          @click="save"
          :disabled="isNameEmpty || loading"
          v-b-tooltip.hover="{ html: true, title: '<b>Como salvar alterações:</b><br>1. Carregue um cenário no PAP<br>2. Faça as alterações desejadas<br>3. Execute a simulação (Play)<br>4. Selecione o cenário aqui e clique em Salvar' }"
        >
          <i class="fa fa-floppy-o"></i> Salvar
        </b-button>
        <div v-if="xmlFile" class="xml-badge-row">
          <b-badge variant="success">{{ xmlFile.name }}</b-badge>
        </div>
        <div v-if="copiedFromName" class="xml-badge-row">
          <b-badge variant="info">Copiado de: {{ copiedFromName }}</b-badge>
        </div>
      </div>

      </div><!-- /panel-right-inner -->
      </transition>
    </div>

    </div><!-- /scenario-manager-panels -->


    <!-- Modais internos -->
    <b-modal id="modalConfirmLoad" title="Carregar cenário no PAP" ok-variant="primary" ok-title="Carregar" cancel-title="Cancelar" @ok="loadToPAP">
      O cenário <strong>{{ scenarioName }}</strong> está carregado no PAP no momento. Deseja substituí-lo pelo cenário <strong>{{ selectedItem && selectedItem.name }}</strong>?
    </b-modal>

    <b-modal id="modalConfirmDeleteScenario" :title="selectedItem && selectedItem.isFolder ? 'Excluir Pasta' : 'Excluir Cenário'" ok-variant="danger" ok-title="Excluir" cancel-title="Cancelar" hide-header-close @ok="deleteItem">
      <template v-if="selectedItem && selectedItem.isFolder">
        Deseja excluir a pasta <strong>{{ selectedItem.name }}</strong> e todos os cenários dentro dela? Esta ação não pode ser desfeita.
      </template>
      <template v-else>
        Deseja excluir o cenário <strong>{{ selectedItem && selectedItem.name }}</strong>? Esta ação não pode ser desfeita.
      </template>
    </b-modal>

    <b-modal id="modalCreateFolder" title="Nova Pasta" ok-title="Criar" cancel-title="Cancelar" @ok="submitCreateFolder">
      <div class="form-group">
        <label class="form-label">Nome</label>
        <b-form-input v-model="newFolderName" placeholder="Nome da pasta" />
      </div>
      <div v-if="hasRtoPermission" class="form-group mt-2">
        <label class="form-label">Tipo</label>
        <b-form-select v-model="newFolderDomain" @change="newFolderParentId = null">
          <b-form-select-option value="PGDI">PGDI</b-form-select-option>
          <b-form-select-option value="RTO">RTO</b-form-select-option>
        </b-form-select>
      </div>
      <div class="form-group mt-2">
        <label class="form-label">Destino</label>
        <b-form-select v-model="newFolderParentId" @change="onNewFolderParentChange">
          <b-form-select-option :value="null">— Raiz —</b-form-select-option>
          <b-form-select-option v-for="folder in folderListForModal" :key="folder.id" :value="folder.id">
            {{ folder.name }}
          </b-form-select-option>
        </b-form-select>
      </div>
    </b-modal>

    <b-modal id="modalRenameItem" :title="renameModalTitle" ok-title="Salvar" cancel-title="Cancelar" @ok="submitRename">
      <b-form-input v-model="renameValue" placeholder="Novo nome" />
    </b-modal>

    <CompararModelosXML v-if="hasRtoPermission" ref="compararModelos" />

    <!-- Rodapé com botão Comparar -->
    <div class="scenario-manager-footer">
      <b-button v-if="hasRtoPermission" variant="outline-secondary" size="sm" class="btn-comparar-modelos" @click="$refs.compararModelos.abrir()">
        <b-icon icon="arrow-left-right" class="mr-1" /> Comparar Modelos XML
      </b-button>
      <span v-if="pendingOnlineActivation" class="footer-online-hint">
        <b-icon icon="exclamation-triangle-fill" class="mr-1" />
        Não é recomendado ativar o ON-LINE com cenários divergentes do PADRAO.
      </span>
    </div>
  </div>
</template>

<script>
import { urlMainAPI, showError, defaultSuccess } from "@/global";
import { SingletonAreas } from "../flowchart/nodes/_service/singletonAreas";
import GetNameFromXml from "./getNameFromXml";
import { mapState } from "vuex";
import axios from "axios";
import { saveAs } from "file-saver";
import LiquorTree from "liquor-tree";
import CompararModelosXML from "./CompararModelosXML.vue";

// IDs de ObjetoXml do RTO cujo import falhou (XML incompatível, sem retorno ou erro/timeout).
// Evita reprocessar o importsimulationscenario (timeout 300s) a cada abertura da Gestão de
// Cenários. Escopo de sessão da SPA — reseta no reload da página (onde um eventual erro
// transitório volta a ser tentado).
const rtoSyncSkipIds = new Set();

export default {
  name: "ScenarioManager",

  components: {
    tree: LiquorTree,
    CompararModelosXML,
  },

  props: {
    preSelectedScenarioId: {
      type: Number,
      default: null,
    },
    pendingOnlineActivation: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapState(["user", "scenarioName", "scenarioManagerAutoSelect", "isOnlineRunning", "activeModel"]),
    // Cenário selecionado está ativo no ON-LINE em execução? (não pode renomear/excluir)
    isSelectedActiveOnline() {
      return !!this.selectedItem && !this.selectedItem.isFolder && this.isOnlineRunning && this.selectedItem.name === this.activeModel;
    },
    isPentagroUser() {
      return !!this.user && (this.user.UserName || "").toLowerCase() === "pentagro";
    },
    hasRtoPermission() {
      return this.user && this.user.Permissions?.rtoconfigurar?.allowed;
    },
    // Gate da sincronização passiva: basta ter qualquer uma das três permissões do RTO.
    hasAnyRtoPermission() {
      const p = this.user && this.user.Permissions;
      return !!(p && (p.rtoconfigurar?.allowed || p.rtoconfigbase?.allowed || p.rtoconfigavancada?.allowed));
    },
    folderList() {
      return this.flatList.filter((i) => i.isFolder && (i.scenarioType ?? 'PGDI') === this.formDomain);
    },
    folderListForModal() {
      return this.flatList.filter((i) => i.isFolder && (i.scenarioType ?? 'PGDI') === this.newFolderDomain);
    },
    isViewingExisting() {
      return !!this.selectedItem && !this.copiedFromId;
    },
    renameModalTitle() {
      return this.selectedItem && this.selectedItem.isFolder ? 'Renomear Pasta' : 'Renomear Cenário';
    },
    formParentName() {
      if (this.formParentId == null) return '— Raiz —';
      const folder = this.flatList.find(i => i.id === this.formParentId);
      return folder ? folder.name : '— Raiz —';
    },
    isCurrentScenarioActiveInRto() {
      const id = this.selectedItem?.rtoObjetoXmlId;
      return id != null && this.rtoStatusMap[id] === 'Ativo';
    },
    // Nome vazio ou só com espaços: bloqueia o salvar
    isNameEmpty() {
      return !this.formName || !this.formName.trim();
    },
  },

  data() {
    return {
      treeData: [],
      flatList: [],
      selectedItem: null,
      formName: "",
      formParentId: null,
      xmlFile: null,
      xmlContent: null,
      copiedFromId: null,
      copiedFromName: null,
      padraoMeta: null,
      rtoStatusMap: {},
      xmlDifferences: [],
      loading: false,
      loadingMessage: "",
      searchText: "",
      newFolderName: "",
      newFolderParentId: null,
      newFolderDomain: "PGDI",
      formDomain: "PGDI",
      renameValue: "",
      skeletonRows: [
        { key: 1,  indent: 4,  width: 70  },
        { key: 2,  indent: 20, width: 110 },
        { key: 3,  indent: 36, width: 90  },
        { key: 4,  indent: 36, width: 130 },
        { key: 5,  indent: 20, width: 95  },
        { key: 6,  indent: 36, width: 115 },
        { key: 7,  indent: 4,  width: 80  },
        { key: 8,  indent: 20, width: 100 },
        { key: 9,  indent: 36, width: 85  },
        { key: 10, indent: 20, width: 120 },
      ],
      treeOptions: {
        fetchData: false,
        propertyNames: { text: "text", children: "children", state: "state", data: "data" },
        dnd: true,
        filter: {
          emptyText: "Nenhum resultado",
          matcher(query, node) {
            return node.text.toLowerCase().includes(query.toLowerCase());
          },
        },
      },
    };
  },

  watch: {
    // Propaga o estado de "ocupado" ao pai para bloquear o fechamento do modal (X / ESC)
    loading(value) {
      this.$emit("busy-change", value);
    },
  },

  methods: {
    async loadTree(silent = false) {
      if (!silent) this.loading = true;
      try {
        const res = await axios.get(`${urlMainAPI}/getscenariotree`);
        this.flatList = this.flattenTree(res.data);
        await this.loadPadraoMeta();
        this.treeData = this.buildVirtualRoots(res.data);
        if (this.hasRtoPermission) this.loadRtoStatus();

        if (this.preSelectedScenarioId) {
          this.$nextTick(() => {
            const node = this.$refs.tree && this.$refs.tree.find((n) => n.data && n.data.id === this.preSelectedScenarioId);
            if (node && node[0]) node[0].select();
          });
        }
      } catch (e) {
        showError("Erro ao carregar árvore de cenários.");
      } finally {
        if (!silent) this.loading = false;
      }

      if (this.scenarioName && !this.selectedItem) {
        const name = this.scenarioName.toUpperCase();
        const item = this.flatList.find(i => !i.isFolder && i.name.toUpperCase() === name);
        if (item) {
          this.selectedItem = item;
          this.formName = item.name;
          this.formParentId = item.parentId;
          this.xmlFile = null;
          this.xmlContent = null;
          if (item.name !== 'PADRAO') {
            try {
              const scenarioRes = await axios.get(`${urlMainAPI}/getscenario/${item.id}`);
              this.checkXmlDifferences(this.extractScenarioXml(scenarioRes.data));
            } catch (e) { /* silencioso */ }
          }
          this.$nextTick(() => {
            const node = this.$refs.tree && this.$refs.tree.find((n) => n.data && n.data.id === item.id);
            if (node && node[0]) node[0].select();
          });
        }
      }
    },

    buildVirtualRoots(items) {
      const pgdi = items.filter(i => (i.scenarioType ?? 'PGDI') === 'PGDI');
      const rto  = items.filter(i => i.scenarioType === 'RTO');
      const roots = [
        {
          text: 'PGDI',
          data: { isVirtualRoot: true, domain: 'PGDI', isFolder: true },
          state: { expanded: true, selectable: false, draggable: false, dropable: false },
          children: this.toTreeNodes(pgdi),
        },
      ];
      if (this.hasRtoPermission) {
        roots.push({
          text: 'RTO',
          data: { isVirtualRoot: true, domain: 'RTO', isFolder: true },
          state: { expanded: true, selectable: false, draggable: false, dropable: false },
          children: this.toTreeNodes(rto),
        });
      }
      return roots;
    },


    flattenTree(nodes) {
      let result = [];
      const walk = (items) => {
        items.forEach((item) => {
          result.push(item);
          if (item.children && item.children.length) walk(item.children);
        });
      };
      walk(nodes);
      return result;
    },

    toTreeNodes(items) {
      return items.map((item) => {
        const isOutdated = !item.isFolder && item.name !== 'PADRAO' && !!this.padraoMeta && !!(
          (item.version && this.compareVersions(item.version, this.padraoMeta.version) < 0) ||
          (item.diagramName && item.diagramName !== this.padraoMeta.diagramName)
        );
        return {
          text: item.name,
          data: { ...item, isOutdated },
          state: { expanded: item.isFolder, selectable: true },
          children: item.children ? this.toTreeNodes(item.children) : [],
        };
      });
    },

    async handleNodeSelect(node) {
      if (node && node.data && node.data.isVirtualRoot) return;
      this.$nextTick(() => {
        const selected = this.$el.querySelector('.tree-node.selected > .tree-content');
        if (selected) selected.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      });
      this.copiedFromId = null;
      this.copiedFromName = null;
      this.xmlDifferences = [];
      this.selectedItem = node ? node.data : null;
      this.formDomain = this.selectedItem?.scenarioType ?? 'PGDI';
      if (this.selectedItem && !this.selectedItem.isFolder) {
        this.formName = this.selectedItem.name;
        this.formParentId = this.selectedItem.parentId;
        this.xmlFile = null;
        this.xmlContent = null;
        if (this.selectedItem.name !== 'PADRAO') {
          try {
            if (!this.padraoMeta) await this.loadPadraoMeta();
            const res = await axios.get(`${urlMainAPI}/getscenario/${this.selectedItem.id}`);
            this.checkXmlDifferences(this.extractScenarioXml(res.data));
          } catch (e) { /* silencioso */ }
        }
      } else if (this.selectedItem && this.selectedItem.isFolder) {
        this.formName = "";
        this.formParentId = this.selectedItem.id;
        this.xmlFile = null;
        this.xmlContent = null;
      }
    },

    async handleDrop(node, dest, position) {
      if (!dest || !dest.data) return;
      // 'drag-on' → soltar dentro da pasta; 'drag-above'/'drag-below' → soltar entre irmãos (usar pai de dest)
      let targetParentId;
      let targetIndex = null;

      if (position === 'drag-on') {
        if (!dest.data.isFolder) {
          await this.loadTree(true);
          return;
        }
        // Nó raiz virtual (PGDI/RTO) → drop na raiz (parentId = null)
        targetParentId = dest.data.isVirtualRoot ? null : dest.data.id;
        // Append ao final da pasta
        targetIndex = dest.children ? dest.children.length : 0;
      } else if (position === 'drag-above' || position === 'drag-below') {
        const parentNode = dest.parent || null;
        const parentData = parentNode && parentNode.data ? parentNode.data : null;
        if (parentData && !parentData.isFolder) {
          await this.loadTree(true);
          return;
        }
        targetParentId = parentData ? (parentData.isVirtualRoot ? null : parentData.id) : null;

        // Calcular índice de inserção: buscar pos de dest dentro dos irmãos usando treeData
        // Quando targetParentId é null e o pai é um nó virtual root, usar os filhos do virtual root
        const siblingList = (targetParentId === null && parentData && parentData.isVirtualRoot)
          ? (parentNode.children || [])
          : this.getSiblingDataList(targetParentId);
        const destIndex = siblingList.findIndex(s => s.data && s.data.id === dest.data.id);
        if (destIndex !== -1) {
          targetIndex = position === 'drag-above' ? destIndex : destIndex + 1;
        }
      } else {
        await this.loadTree(true);
        return;
      }

      // Item bloqueado só pode ser reordenado na raiz — nunca mover para dentro de pasta
      if (node.data.isLocked && targetParentId !== null) {
        await this.loadTree(true);
        return;
      }

      // Determina domínio do destino para enviar ao backend (atualiza scenarioType do item)
      let destDomain;
      if (position === 'drag-on') {
        destDomain = dest.data.isVirtualRoot ? dest.data.domain : (dest.data.scenarioType ?? 'PGDI');
      } else {
        const parentData = dest.parent && dest.parent.data ? dest.parent.data : null;
        destDomain = parentData
          ? (parentData.isVirtualRoot ? parentData.domain : (parentData.scenarioType ?? 'PGDI'))
          : 'PGDI';
      }

      try {
        await axios.post(`${urlMainAPI}/movescenarioitem/${node.data.id}`, { parentId: targetParentId, targetIndex, scenarioType: destDomain });
        defaultSuccess("Item movido com sucesso.");
        // Atualiza estado local imediatamente — a árvore já está visualmente correta após o drop
        const movedFlat = this.flatList.find(i => i.id === node.data.id);
        if (movedFlat) {
          movedFlat.scenarioType = destDomain;
          movedFlat.parentId = targetParentId ?? null;
        }
        if (this.selectedItem && this.selectedItem.id === node.data.id) {
          this.selectedItem = movedFlat ?? this.selectedItem;
          this.formDomain = destDomain;
        }
        // Reload silencioso em background para manter consistência (sortOrder, etc.)
        this.loadTree(true);
      } catch (e) {
        showError(e.response && e.response.data ? e.response.data : "Erro ao mover item.");
        await this.loadTree(true);
      }
    },

    getSiblingDataList(parentId) {
      if (parentId === null || parentId === undefined) {
        return this.treeData;
      }
      const find = (nodes) => {
        for (const n of nodes) {
          if (n.data && n.data.id === parentId) return n.children || [];
          if (n.children) {
            const found = find(n.children);
            if (found) return found;
          }
        }
        return [];
      };
      return find(this.treeData);
    },

    newScenario() {
      this.selectedItem = null;
      this.formName = "";
      this.formParentId = null;
      this.formDomain = "PGDI";
      this.xmlFile = null;
      this.xmlContent = null;
      this.copiedFromId = null;
      this.copiedFromName = null;
      this.xmlDifferences = [];
    },

    copyScenario() {
      if (!this.selectedItem || this.selectedItem.isFolder) return;
      this.copiedFromId = this.selectedItem.id;
      this.copiedFromName = this.selectedItem.name;
      this.formName = "";
      this.formParentId = this.selectedItem.parentId;
      this.selectedItem = null;
      this.$nextTick(() => {
        const nameInput = this.$el.querySelector("input.input-pentagro");
        if (nameInput) nameInput.focus();
      });
    },

    clearForm() {
      this.selectedItem = null;
      this.formName = "";
      this.formParentId = null;
      this.formDomain = "PGDI";
      this.xmlFile = null;
      this.xmlContent = null;
      this.copiedFromId = null;
      this.copiedFromName = null;
      this.xmlDifferences = [];
    },

    handleXmlFile(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.xmlFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.xmlContent = e.target.result;
        this.checkXmlDifferences(this.xmlContent);
        this.compareImportedAgainstPadrao(this.xmlContent, file.name);
      };
      reader.readAsText(file, "UTF-8");
    },

    async compareImportedAgainstPadrao(xmlImportado, fileName) {
      if (!this.hasRtoPermission) return;
      if (this.formDomain !== 'RTO') return;
      if (!this.$refs.compararModelos) return;
      // Padrão PAP = cenário PADRAO no domínio PGDI
      const padrao = this.flatList.find(
        i => !i.isFolder && i.name === 'PADRAO' && (i.scenarioType ?? 'PGDI') === 'PGDI'
      );
      if (!padrao) return;
      try {
        const res = await axios.get(`${urlMainAPI}/getscenario/${padrao.id}`);
        const xmlPadrao = this.extractScenarioXml(res.data);
        if (!xmlPadrao) return;
        this.$refs.compararModelos.abrirComStrings({
          xmlPAP: xmlPadrao,
          xmlRTO: xmlImportado,
          labelPAP: 'PADRAO (PAP)',
          labelRTO: `${fileName} (RTO)`,
        });
      } catch (e) {
        console.warn('Não foi possível comparar XML importado com PADRAO:', e);
      }
    },

    extractScenarioXml(data) {
      if (!data) return null;
      // res.data pode ser objeto { id, name, scenario } ou string JSON (double-serialized)
      if (typeof data === 'string') {
        try { return JSON.parse(data)?.scenario ?? null; } catch (e) { return null; }
      }
      return data.scenario ?? null;
    },

    getXmlMeta(xmlString) {
      if (!xmlString) return null;
      try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(xmlString, "application/xml");
        const sim = doc.getElementsByTagName("Simulation")[0];
        if (!sim) return null;
        return {
          version: sim.getAttribute("version"),
          diagramName: sim.getAttribute("diagram_name"),
        };
      } catch (e) {
        return null;
      }
    },

    async loadPadraoMeta() {
      const padrao = this.flatList.find(i => !i.isFolder && i.name === "PADRAO");
      if (!padrao) return;
      if (padrao.version !== undefined && padrao.diagramName !== undefined) {
        this.padraoMeta = { version: padrao.version, diagramName: padrao.diagramName };
        return;
      }
      try {
        const res = await axios.get(`${urlMainAPI}/getscenario/${padrao.id}`);
        this.padraoMeta = this.getXmlMeta(this.extractScenarioXml(res.data));
      } catch (e) { /* silencioso */ }
    },

    compareVersions(v1, v2) {
      if (!v1 || !v2) return 0;
      const parts1 = v1.split('.').map(Number);
      const parts2 = v2.split('.').map(Number);
      const len = Math.max(parts1.length, parts2.length);
      for (let i = 0; i < len; i++) {
        const a = parts1[i] || 0;
        const b = parts2[i] || 0;
        if (a !== b) return a - b;
      }
      return 0;
    },

    checkXmlDifferences(xmlString) {
      if (!this.padraoMeta || !xmlString) { this.xmlDifferences = []; return; }
      const meta = this.getXmlMeta(xmlString);
      if (!meta) { this.xmlDifferences = []; return; }
      const diffs = [];
      if (this.compareVersions(meta.version, this.padraoMeta.version) < 0)
        diffs.push({ label: "Versão desatualizada", padrao: this.padraoMeta.version, cenario: meta.version });
      if (meta.diagramName !== this.padraoMeta.diagramName)
        diffs.push({ label: "Diagrama diferente do PADRAO", padrao: this.padraoMeta.diagramName, cenario: meta.diagramName });
      this.xmlDifferences = diffs;
    },

    confirmLoad() {
      if (!this.selectedItem || this.selectedItem.isFolder) return;
      if (this.scenarioName && this.scenarioName !== this.selectedItem.name) {
        this.$bvModal.show("modalConfirmLoad");
      } else {
        this.loadToPAP();
      }
    },

    handleDblClick(node) {
      if (!node || !node.data || node.data.isFolder) return;
      if (node.data.isCompatible === false) return;
      this.selectedItem = node.data;
      this.formName = node.data.name;
      this.confirmLoad();
    },

    async loadToPAP() {
      if (!this.selectedItem || this.selectedItem.isFolder) return;
      this.loading = true;
      this.loadingMessage = this.pendingOnlineActivation ? "Ativando ON-LINE..." : "Carregando cenário...";
      const wasImport = !!this.xmlContent;
      try {
        let resData;

        const longTimeout = { timeout: 300000 };
        if (wasImport) {
          // XML importado pendente: simular para obter resultado compatível
          const res = await axios.post(`${urlMainAPI}/importsimulationscenario`, { scenario: this.xmlContent }, longTimeout);
          resData = { ...res.data, name: this.formName };
        } else {
          const res = await axios.post(`${urlMainAPI}/loadsimulationscenario`, { name: this.selectedItem.name }, longTimeout);
          resData = res.data;
        }

        const xml = {
          name: resData.name,
          nameXml: GetNameFromXml(JSON.stringify(resData.scenario)),
          scenario: resData.scenario,
          scenarioResult: resData.scenarioResult,
          scenarioObjectStructure: resData.scenarioObjectStructure,
        };
        if (!resData.scenarioObjectStructure) {
          showError("Não foi possível carregar o cenário: estrutura incompatível com a versão atual.");
          return;
        }

        const xmlScenarioStr = typeof resData.scenario === 'string' ? resData.scenario : null;
        const loadedMeta = this.getXmlMeta(xmlScenarioStr);
        const isOutdated = !!(loadedMeta && this.padraoMeta && (
          this.compareVersions(loadedMeta.version, this.padraoMeta.version) < 0 ||
          loadedMeta.diagramName !== this.padraoMeta.diagramName
        ));
        this.$store.commit("setXML", xml);
        localStorage.setItem("xml", JSON.stringify(xml));
        this.$store.commit("setScenarioName", xml.name);
        this.$store.commit("setScenarioOutdated", isOutdated);
        localStorage.setItem("scenarioOutdated", String(isOutdated));
        this.$store.commit("setCanUpdateFormLoss", !SingletonAreas.canLoadFormLoss);
        SingletonAreas.canLoadFormLoss = !SingletonAreas.canLoadFormLoss;
        localStorage.setItem("lastScenarioName", xml.name);
        defaultSuccess(`Cenário "${xml.name}" carregado no PAP.`);
        this.$emit("scenarioLoaded");
        this.$bvModal.hide("modalScenarioManager");

        SingletonAreas.LoadScenario(resData.scenarioObjectStructure, false)
          .catch(err => console.warn("Aviso ao recarregar diagrama:", err));
      } catch (e) {
        showError(e.response && e.response.data ? e.response.data : "Erro ao carregar cenário.");
      } finally {
        this.loading = false;
        this.loadingMessage = "";
      }
    },

    // Nome reservado do cenário-modelo PADRAO (normaliza acento/caixa, igual ao backend)
    isReservedScenarioName(name) {
      return (name || "").trim().normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase() === "PADRAO";
    },

    async save() {
      // Fecha o tooltip do botão Salvar para que ele não fique preso sobre o overlay de
      // loading (o mouseleave não dispara quando o overlay aparece sob o cursor).
      this.$root.$emit("bv::hide::tooltip");
      if (this.isNameEmpty) {
        showError("Informe um nome — não é possível salvar com o nome vazio.");
        return;
      }

      // Cenário reservado PADRAO: apenas o usuário Pentagro pode criar ou sobrescrever.
      if (this.isReservedScenarioName(this.formName) && !this.isPentagroUser) {
        showError("\"PADRAO\" é um nome reservado do sistema. Escolha outro nome.");
        return;
      }

      // Verifica se já existe cenário com o mesmo nome
      const trimmedName = this.formName.trim().toLowerCase();
      const allDuplicates = this.flatList.filter(
        (n) => !n.isFolder && n.name?.toLowerCase() === trimmedName
          && (!this.selectedItem || n.id !== this.selectedItem.id)
      );

      if (allDuplicates.length > 0) {
        const duplicateInSameFolder = allDuplicates.find((n) => n.parentId === this.formParentId);
        const duplicateInOtherFolder = allDuplicates.find((n) => n.parentId !== this.formParentId);

        if (duplicateInOtherFolder) {
          await this.$bvModal.msgBoxOk(
            `Já existe um cenário com o nome "${this.formName}". Altere o nome antes de salvar.`,
            {
              title: "Cenário duplicado",
              okTitle: "Entendi",
              okVariant: "primary",
            }
          );
          return;
        }

        if (duplicateInSameFolder) {
          const confirm = await this.$bvModal.msgBoxConfirm(
            `Já existe um cenário com o nome "${this.formName}" nesta pasta. Deseja sobrescrever?`,
            {
              title: "Cenário duplicado",
              okTitle: "Sobrescrever",
              cancelTitle: "Cancelar",
              okVariant: "danger",
            }
          );
          if (!confirm) return;
        }
      }

      const wasImport = !!this.xmlContent;
      this.loading = true;
      this.loadingMessage = "";
      try {
        const storeXml = this.$store.state.xml;

        // ── Fluxo de importação: simula → salva com resultado → carrega no PAP ──
        if (wasImport) {
          this.loadingMessage = "Importando e simulando cenário... Isso pode levar alguns segundos.";
          const importRes = await axios.post(`${urlMainAPI}/importsimulationscenario`, { scenario: this.xmlContent }, { timeout: 300000 });
          this.loadingMessage = "Salvando cenário...";
          const importData = importRes.data;

          if (!importData.scenarioObjectStructure) {
            showError("Não foi possível simular o cenário: estrutura incompatível com a versão atual.");
            return;
          }

          // Domínio RTO: envia o XML cru para o RTO. Falha não bloqueia o salvamento local.
          let rtoObjetoXmlId = this.selectedItem?.rtoObjetoXmlId ?? null;
          if (this.formDomain === "RTO" && !rtoObjetoXmlId) {
            this.loadingMessage = "Enviando XML para o RTO...";
            try {
              const rtoRes = await axios.post(`${urlMainAPI}/configurator/rto/objetoxml/importar`, { xml: this.xmlContent });
              rtoObjetoXmlId = rtoRes.data?.data?.id ?? null;
            } catch (e) {
              showError(`Cenário será salvo no PGDI, mas não foi enviado ao RTO: ${this.extractRtoError(e)}. Salve o cenário novamente para reenviar.`);
            }
          }

          this.loadingMessage = "Salvando cenário...";
          await axios.post(`${urlMainAPI}/savesimulationscenario?userName=${encodeURIComponent(this.user.UserName)}`, {
            name: this.formName,
            scenario: importData.scenario,
            scenarioResult: importData.scenarioResult || null,
            parentId: this.formParentId,
            scenarioType: this.formDomain,
            rtoObjetoXmlId,
          });

          const xml = {
            name: this.formName,
            nameXml: GetNameFromXml(JSON.stringify(importData.scenario)),
            scenario: importData.scenario,
            scenarioResult: importData.scenarioResult,
            scenarioObjectStructure: importData.scenarioObjectStructure,
          };
          // Recarrega árvore e padraoMeta antes de calcular isOutdated
          await this.loadTree();

          const loadedMeta = this.getXmlMeta(typeof importData.scenario === "string" ? importData.scenario : null);
          const isOutdated = !!(loadedMeta && this.padraoMeta && (
            this.compareVersions(loadedMeta.version, this.padraoMeta.version) < 0 ||
            loadedMeta.diagramName !== this.padraoMeta.diagramName
          ));
          this.$store.commit("setXML", xml);
          localStorage.setItem("xml", JSON.stringify(xml));
          this.$store.commit("setScenarioName", xml.name);
          this.$store.commit("setScenarioOutdated", isOutdated);
          localStorage.setItem("scenarioOutdated", String(isOutdated));
          this.$store.commit("setCanUpdateFormLoss", !SingletonAreas.canLoadFormLoss);
          SingletonAreas.canLoadFormLoss = !SingletonAreas.canLoadFormLoss;
          localStorage.setItem("lastScenarioName", xml.name);
          this.xmlFile = null;
          this.xmlContent = null;
          this.copiedFromId = null;
          this.copiedFromName = null;
          defaultSuccess(`Cenário "${this.formName}" salvo e carregado no PAP.`);
          this.$emit("scenarioLoaded");
          this.$bvModal.hide("modalScenarioManager");
          SingletonAreas.LoadScenario(importData.scenarioObjectStructure, false)
            .catch((err) => console.warn("Aviso ao recarregar diagrama:", err));
          return;
        }

        // ── Fluxo normal (cópia ou cenário do PAP) ──
        let scenarioXml = null;
        if (this.copiedFromId) {
          const res = await axios.get(`${urlMainAPI}/getscenario/${this.copiedFromId}`);
          scenarioXml = res.data?.scenario || null;
          if (!scenarioXml) {
            showError("O cenário original não possui XML salvo.");
            return;
          }
        } else {
          scenarioXml = storeXml && storeXml.scenario;
          if (!scenarioXml) {
            showError("Nenhum cenário disponível para salvar. Carregue um cenário no PAP antes de salvar.");
            return;
          }
        }

        const obj = {
          name: this.formName,
          scenario: scenarioXml,
          scenarioResult: this.copiedFromId ? null : ((storeXml && storeXml.scenarioResult) || null),
          parentId: this.formParentId,
          scenarioType: this.formDomain,
        };
        await axios.post(`${urlMainAPI}/savesimulationscenario?userName=${encodeURIComponent(this.user.UserName)}`, obj);
        this.$store.commit("setScenarioName", this.formName);
        this.xmlFile = null;
        this.xmlContent = null;
        this.copiedFromId = null;
        this.copiedFromName = null;
        defaultSuccess(`Cenário "${this.formName}" salvo.`);
        this.$emit("scenarioLoaded");
        await this.loadTree();

        // RTO: atualiza o ObjetoXml vinculado a este cenário com o XML salvo (in-place).
        await this.syncObjetoXmlRto(scenarioXml, this.selectedItem && this.selectedItem.rtoObjetoXmlId);
      } catch (e) {
        showError(e.response && e.response.data ? e.response.data : "Erro ao salvar cenário.");
      } finally {
        this.loading = false;
        this.loadingMessage = "";
      }
    },

    // Atualiza IN-PLACE o ObjetoXml do RTO vinculado ao cenário (rota AtualizarXml: o RTO re-valida
    // via TCP). Só roda pra cenário de domínio RTO já vinculado a um ObjetoXml. Best-effort: o cenário
    // já foi salvo; se a sync falhar (simulador rejeita/offline), avisa e segue — não desfaz o save.
    async syncObjetoXmlRto(scenarioXml, objetoXmlId) {
      const xml = typeof scenarioXml === "string" ? scenarioXml.trim() : "";
      if (!xml || this.formDomain !== "RTO" || !objetoXmlId) return;
      try {
        this.loadingMessage = "Atualizando modelo no RTO...";
        await axios.put(`${urlMainAPI}/configurator/rto/objetoxml/${objetoXmlId}/atualizar-xml`, { xml });
        defaultSuccess(`Modelo do RTO atualizado com o cenário salvo (ObjetoXml #${objetoXmlId}).`);
      } catch (e) {
        showError(`Cenário salvo, mas falhou ao atualizar o modelo no RTO: ${this.extractRtoError(e)}`);
      }
    },

    confirmDelete() {
      if (!this.selectedItem || !this.selectedItem.isFolder) return;
      this.$bvModal.show("modalConfirmDeleteScenario");
    },

    confirmDeleteScenario() {
      if (!this.selectedItem || this.selectedItem.isFolder) return;
      this.$bvModal.show("modalConfirmDeleteScenario");
    },

    async deleteItem() {
      if (!this.selectedItem) return;
      this.loading = true;
      try {
        // Cenário vinculado a um ObjetoXml do RTO: exclui também no RTO antes do delete local.
        const rtoId = this.selectedItem.rtoObjetoXmlId;
        if (rtoId) {
          try {
            await axios.delete(`${urlMainAPI}/configurator/rto/objetoxml/${rtoId}`);
          } catch (e) {
            const status = e.response && e.response.status;
            if (status === 404) {
              // ObjetoXml já não existe no RTO — segue com a exclusão local.
            } else if (status === 400) {
              showError("Este modelo está ATIVO no RTO. Inative-o no RTO antes de excluir.");
              return;
            } else {
              showError("Erro ao excluir o modelo no RTO: " + this.extractRtoError(e));
              return;
            }
          }
        }
        const deletedName = this.selectedItem.name;
        const wasLoaded = !this.selectedItem.isFolder && deletedName === this.scenarioName;
        await axios.post(`${urlMainAPI}/deletescenarioitem/${this.selectedItem.id}`);
        defaultSuccess("Item excluído com sucesso.");
        // Se excluiu o cenário carregado no PAP, limpa o nome carregado (evita ativar com cenário inexistente)
        if (wasLoaded) {
          localStorage.removeItem("xml");
          localStorage.removeItem("lastScenarioName");
          this.$store.commit("setXML", null);
          this.$store.commit("setScenarioName", "Não informado");
          showError("O cenário carregado no PAP foi excluído. Carregue outro cenário antes de ativar o ON-LINE.");
        }
        this.clearForm();
        await this.loadTree();
      } catch (e) {
        showError(e.response && e.response.data ? e.response.data : "Erro ao excluir item.");
      } finally {
        this.loading = false;
      }
    },

    async loadRtoStatus() {
      try {
        const res = await axios.get(`${urlMainAPI}/configurator/rto/objetoxml/lista`);
        const list = res.data?.data ?? res.data ?? [];
        const map = {};
        for (const obj of list) {
          if (obj && obj.id != null) map[obj.id] = obj.status;
        }
        this.rtoStatusMap = map;
      } catch (e) {
        // RTO inacessível ou erro de rede — UI degrada sem ícone, sem toast.
        this.rtoStatusMap = {};
      }
    },

    async activateRtoScenario() {
      if (!this.selectedItem?.rtoObjetoXmlId) return;
      const ok = await this.$bvModal.msgBoxConfirm(
        `Ativar "${this.selectedItem.name}" como modelo no RTO desativa o XML que está ativo atualmente. Continuar?`,
        {
          title: 'Ativar no RTO?',
          okTitle: 'Ativar',
          cancelTitle: 'Cancelar',
          okVariant: 'success',
          centered: true,
        }
      );
      if (!ok) return;
      this.loading = true;
      try {
        await axios.put(`${urlMainAPI}/configurator/rto/objetoxml/${this.selectedItem.rtoObjetoXmlId}/ativar`);
        defaultSuccess(`Cenário "${this.selectedItem.name}" ativado no RTO.`);
        await this.loadRtoStatus();
      } catch (e) {
        showError("Erro ao ativar no RTO: " + this.extractRtoError(e));
      } finally {
        this.loading = false;
      }
    },

    extractRtoError(e) {
      const d = e && e.response && e.response.data;
      if (!d) return (e && e.message) || "erro desconhecido";
      if (typeof d === "string") return d;
      return d.description || d.descricaoErro || d.message || JSON.stringify(d);
    },

    uniqueScenarioName(base) {
      const taken = (n) => this.flatList.some(
        (i) => !i.isFolder && (i.name || "").toUpperCase() === n.toUpperCase()
      );
      let name = base;
      let i = 2;
      while (taken(name)) { name = `${base} (${i})`; i++; }
      return name;
    },

    // silent=true: sincronização passiva ao abrir a Gestão de Cenários (sem overlay,
    // sem toast de sucesso e sem alarmar se o RTO estiver indisponível).
    async syncWithRto(silent = false) {
      if (!silent) {
        this.loading = true;
        this.loadingMessage = "Sincronizando com o RTO...";
      }
      try {
        // 1. Lista os ObjetoXml existentes no RTO.
        // skipGlobalErrorHandler: a sync passiva não deve disparar o toast global
        // (dump do .NET em 500 / redirect em Network Error) quando o RTO está fora.
        const listaRes = await axios.get(`${urlMainAPI}/configurator/rto/objetoxml/lista`, { skipGlobalErrorHandler: true });
        const rtoList = (listaRes.data && listaRes.data.data) || [];
        const rtoIds = new Set(rtoList.map((o) => o.id));

        // 2. Cenários RTO do PGDI já vinculados a um ObjetoXml.
        const linkedScenarios = this.flatList.filter(
          (i) => !i.isFolder && i.scenarioType === "RTO" && i.rtoObjetoXmlId != null
        );
        const linkedIds = new Set(linkedScenarios.map((i) => i.rtoObjetoXmlId));

        let imported = 0;
        let removed = 0;

        // 3. Pull — ObjetoXml do RTO ainda sem cenário correspondente no PGDI.
        for (const obj of rtoList) {
          if (linkedIds.has(obj.id)) continue;
          if (rtoSyncSkipIds.has(obj.id)) continue; // já falhou antes nesta sessão — não reprocessa
          try {
            const xmlRes = await axios.get(`${urlMainAPI}/configurator/rto/objetoxml/${obj.id}/xml`, { skipGlobalErrorHandler: true });
            const rawXml = xmlRes.data && xmlRes.data.data && xmlRes.data.data.xml;
            if (!rawXml) { rtoSyncSkipIds.add(obj.id); continue; }
            const importRes = await axios.post(
              `${urlMainAPI}/importsimulationscenario`,
              { scenario: rawXml },
              { timeout: 300000, skipGlobalErrorHandler: true }
            );
            const importData = importRes.data;
            if (!importData.scenarioObjectStructure) { rtoSyncSkipIds.add(obj.id); continue; }
            await axios.post(`${urlMainAPI}/savesimulationscenario?userName=${encodeURIComponent(this.user.UserName)}`, {
              name: this.uniqueScenarioName(obj.nomeXml || `RTO ${obj.id}`),
              scenario: importData.scenario,
              scenarioResult: importData.scenarioResult || null,
              parentId: null,
              scenarioType: "RTO",
              rtoObjetoXmlId: obj.id,
            }, { skipGlobalErrorHandler: true });
            imported++;
          } catch (e) {
            rtoSyncSkipIds.add(obj.id); // evita repetir o import pesado (timeout 300s) a cada open
            console.warn(`Falha ao importar ObjetoXml ${obj.id} do RTO:`, e);
          }
        }

        // 4. Reconciliação de exclusão — cenário PGDI cujo ObjetoXml sumiu do RTO.
        // Guarda de segurança: só reconcilia se a lista do RTO veio não-vazia. Uma lista
        // vazia (RTO recém-resetado, instância errada, 200 vazio transitório) excluiria
        // TODOS os cenários RTO vinculados — perigoso agora que a sync roda automática e
        // silenciosa. Lista vazia → não exclui nada (no pior caso ficam órfãos inofensivos).
        if (rtoList.length) {
          for (const sc of linkedScenarios) {
            if (rtoIds.has(sc.rtoObjetoXmlId)) continue;
            try {
              await axios.post(`${urlMainAPI}/deletescenarioitem/${sc.id}`, null, { skipGlobalErrorHandler: true });
              removed++;
            } catch (e) {
              console.warn(`Falha ao remover cenário ${sc.id}:`, e);
            }
          }
        }

        // Só recarrega a árvore se a sync de fato alterou algo — evita um segundo
        // loadTree completo no caso comum (abertura sem nada a sincronizar).
        if (imported || removed) await this.loadTree(silent);
        if (!silent) defaultSuccess(`Sincronização concluída: ${imported} importado(s) do RTO, ${removed} removido(s).`);
      } catch (e) {
        if (silent) console.warn("Sincronização automática com o RTO falhou:", this.extractRtoError(e));
        else showError("Erro ao sincronizar com o RTO: " + this.extractRtoError(e));
      } finally {
        if (!silent) {
          this.loading = false;
          this.loadingMessage = "";
        }
      }
    },

    createFolder() {
      this.newFolderName = "";
      this.newFolderParentId = this.selectedItem && this.selectedItem.isFolder ? this.selectedItem.id : null;
      this.newFolderDomain = this.selectedItem ? (this.selectedItem.scenarioType ?? 'PGDI') : this.formDomain;
      this.$bvModal.show("modalCreateFolder");
    },

    async submitCreateFolder() {
      if (!this.newFolderName) {
        showError("Informe o nome da pasta.");
        return;
      }
      // Nome reservado PADRAO: bloqueado para todos (colide com o lookup por nome que ignora isFolder)
      if (this.isReservedScenarioName(this.newFolderName)) {
        showError("\"PADRAO\" é um nome reservado do sistema. Escolha outro nome.");
        return;
      }
      this.loading = true;
      try {
        const parentId = this.newFolderParentId;
        await axios.post(`${urlMainAPI}/createscenariofolder`, { name: this.newFolderName, parentId, scenarioType: this.newFolderDomain });
        defaultSuccess(`Pasta "${this.newFolderName}" criada.`);
        await this.loadTree();
      } catch (e) {
        showError(e.response && e.response.data ? e.response.data : "Erro ao criar pasta.");
      } finally {
        this.loading = false;
      }
    },

    onNewFolderParentChange(parentId) {
      if (parentId) {
        const parent = this.flatList.find(i => i.id === parentId);
        if (parent) this.newFolderDomain = parent.scenarioType ?? 'PGDI';
      }
    },

    renameItem() {
      if (!this.selectedItem || !this.selectedItem.isFolder) return;
      this.renameValue = this.selectedItem.name;
      this.$bvModal.show("modalRenameItem");
    },

    renameScenario() {
      if (!this.selectedItem || this.selectedItem.isFolder) return;
      this.renameValue = this.selectedItem.name;
      this.$bvModal.show("modalRenameItem");
    },

    async submitRename() {
      if (!this.renameValue) {
        showError("Informe o novo nome.");
        return;
      }
      // Nome reservado PADRAO: impede criar um homônimo via rename (apenas Pentagro pode)
      if (this.isReservedScenarioName(this.renameValue) && !this.isPentagroUser) {
        showError("\"PADRAO\" é um nome reservado do sistema. Escolha outro nome.");
        return;
      }
      // PADRAO é único no sistema inteiro (os lookups por nome ignoram a pasta).
      // Bloqueia criar um 2º PADRAO em qualquer pasta — inclusive para o Pentagro.
      if (this.isReservedScenarioName(this.renameValue)) {
        const padraoExists = this.flatList.some(
          (n) => !n.isFolder && this.isReservedScenarioName(n.name)
            && (!this.selectedItem || n.id !== this.selectedItem.id)
        );
        if (padraoExists) {
          showError("Já existe o cenário PADRAO no sistema. Só pode existir um.");
          return;
        }
      }
      this.loading = true;
      const oldName = this.selectedItem ? this.selectedItem.name : null;
      try {
        await axios.post(`${urlMainAPI}/renamescenarioitem/${this.selectedItem.id}?userName=${encodeURIComponent(this.user.UserName)}`, { name: this.renameValue });
        defaultSuccess("Item renomeado com sucesso.");
        this.formName = this.renameValue;
        if (this.selectedItem) this.selectedItem.name = this.renameValue;
        // Se renomeou o cenário carregado no PAP, sincroniza nome no store/localStorage (evita ativar com nome antigo)
        if (this.selectedItem && !this.selectedItem.isFolder && oldName && oldName === this.scenarioName) {
          this.syncLoadedScenarioName(this.renameValue);
        }
        await this.loadTree(true);
      } catch (e) {
        // Toast já exibido pelo interceptor global do axios (config/axios.js) para
        // status 400/409/429/500 — não chamamos showError aqui para evitar toast duplicado.
        // Mantém o erro no console para diagnóstico.
        console.error("Falha ao renomear item:", e);
      } finally {
        this.loading = false;
      }
    },

    // Mantem o nome do cenario carregado (store + localStorage) sincronizado apos rename
    syncLoadedScenarioName(newName) {
      try {
        const xml = localStorage.xml ? JSON.parse(localStorage.xml) : null;
        if (xml) {
          xml.name = newName;
          localStorage.setItem("xml", JSON.stringify(xml));
          this.$store.commit("setXML", xml);
        }
        this.$store.commit("setScenarioName", newName);
        localStorage.setItem("lastScenarioName", newName);
      } catch (e) { /* localStorage.xml ausente/invalido — nada a sincronizar */ }
    },

    async exportItem() {
      if (!this.selectedItem || this.selectedItem.isFolder) return;
      this.loading = true;
      try {
        const res = await axios.get(`${urlMainAPI}/exportscenario/${this.selectedItem.id}`, { responseType: "text" });
        const xmlText = res.data;
        const meta = this.getXmlMeta(xmlText);
        const filename = (meta && meta.diagramName) ? `${meta.diagramName}.xml` : `${this.selectedItem.name}.xml`;
        saveAs(new Blob([xmlText], { type: "application/xml" }), filename);
      } catch (e) {
        showError("Erro ao exportar cenário.");
      } finally {
        this.loading = false;
      }
    },
  },

  async created() {
    await this.loadTree();
    // Sincronização passiva com o RTO ao abrir, para quem tem qualquer permissão do RTO.
    if (this.hasAnyRtoPermission) this.syncWithRto(true);
  },

};
</script>

<style scoped>
.scenario-manager {
  display: flex;
  flex-direction: column;
  gap: 0;
  height: min(560px, calc(85vh - 60px));
  position: relative;
}

/* Overlay de bloqueio durante operações (carregar/ativar ON-LINE) */
.scenario-blocking-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(1px);
  cursor: progress;
}

.scenario-blocking-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.scenario-blocking-spinner {
  width: 2.4rem;
  height: 2.4rem;
  color: #00A39F;
}

.scenario-blocking-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: #495057;
  animation: textPulse 1.4s infinite ease-in-out;
}

.scenario-manager > .panel-left,
.scenario-manager > .panel-right {
  flex: 1;
}

/* Layout interno dos painéis lado a lado */
.scenario-manager-panels {
  display: flex;
  gap: 16px;
  flex: 1;
  overflow: hidden;
  height: min(520px, calc(85vh - 100px));
}

/* Rodapé */
.scenario-manager-footer {
  border-top: 1px solid #e9ecef;
  background: #f8f9fa;
  padding: 8px 12px;
  margin: 8px -16px -8px;
  display: flex;
  align-items: center;
  border-radius: 0 0 6px 6px;
  position: relative;
}

.footer-online-hint {
  margin-left: 1rem;
  font-size: 0.78rem;
  color: #dc3545;
  font-weight: 600;
}


.btn-comparar-modelos {
  font-size: 0.8rem;
  color: #f37021 !important;
  border-color: #f37021 !important;
  background: #fff !important;
}

.btn-comparar-modelos:hover {
  background: #f37021 !important;
  border-color: #f37021 !important;
  color: #fff !important;
}

/* (estilos do modal de comparação movidos para CompararModelosXML.vue) */

.panel-left {
  width: 420px;
  min-width: 350px;
  display: flex;
  flex-direction: column;
  position: relative;
  border-right: none;
  padding-right: 12px;
  overflow: hidden;
}

.panel-left::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 1px;
  height: 100%;
  background: linear-gradient(to bottom, transparent 0%, #dee2e6 15%, #dee2e6 85%, transparent 100%);
}


.panel-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
}

.panel-right-inner {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.panel-fade-enter-active {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

.panel-fade-enter {
  opacity: 0;
  transform: translateY(6px);
}


.toolbar {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 8px;
  color: #adb5bd;
  font-size: 0.8rem;
  pointer-events: none;
  transition: color 0.15s ease;
}

.search-wrapper:focus-within .search-icon {
  color: #ff5f00;
}

.search-input {
  width: 100%;
  font-size: 0.85rem;
  padding-left: 26px;
  padding-right: 26px;
  height: 31px;
  border-radius: 3px;
  border: 1px solid #ff5f00;
  outline: none;
  color: #495057;
  background: #fff;
}

.search-input:focus {
  border-color: #ff5f00;
  box-shadow: 0 0 0 2px rgba(255, 95, 0, 0.2);
}

.search-clear {
  position: absolute;
  right: 5px;
  background: none;
  border: none;
  padding: 0 2px;
  color: #adb5bd;
  cursor: pointer;
  display: flex;
  align-items: center;
  font-size: 1rem;
  line-height: 1;
}

.search-clear:hover {
  color: #495057;
}

@keyframes shimmer {
  0%   { background-position: -300px 0; }
  100% { background-position: 300px 0; }
}

.shimmer {
  background: linear-gradient(90deg, #e9ecef 25%, #f8f9fa 50%, #e9ecef 75%);
  background-size: 600px 100%;
  animation: shimmer 1.4s infinite linear;
  border-radius: 3px;
  display: inline-block;
}

.skeleton-tree {
  padding: 6px 4px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.skeleton-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.skeleton-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  border-radius: 3px;
}

.skeleton-label {
  height: 12px;
}

@keyframes textPulse {
  0%, 100% { opacity: 0.4; }
  50%       { opacity: 1; }
}

.skeleton-loading-message {
  margin-top: 10px;
  font-size: 0.8rem;
  color: #6c757d;
  text-align: center;
  animation: textPulse 1.4s ease-in-out infinite;
}

.tree-container {
  flex: 1;
  overflow: auto;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 4px;
  min-height: 0;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.06);
}

.tree-container::-webkit-scrollbar {
  width: 5px;
  height: 5px;
}

.tree-container::-webkit-scrollbar-track {
  background: transparent;
}

.tree-container::-webkit-scrollbar-thumb {
  background: #ced4da;
  border-radius: 4px;
}

.tree-container::-webkit-scrollbar-thumb:hover {
  background: #adb5bd;
}

.tree-node-slot {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.88rem;
  white-space: nowrap;
}

.folder-icon {
  color: #ff5f00;
}

.file-icon {
  color: #6c757d;
}

.incompatible-icon {
  color: #dc3545;
  margin-left: 4px;
}

.outdated-icon {
  color: #fd7e14;
  margin-left: 4px;
}

.rto-active-icon {
  color: #16a34a;
  margin-left: 4px;
}

.locked-node {
  color: #6c757d;
  font-style: italic;
}

.editor-toolbar {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.incompatibility-banner {
  background-color: #fffbf0;
  border: none;
  border-left: 4px solid #ffc107;
  border-radius: 0 6px 6px 0;
  padding: 8px 12px;
  font-size: 0.85rem;
  color: #856404;
  margin-bottom: 10px;
}

.banner-slide-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.banner-slide-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.banner-slide-enter,
.banner-slide-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}

.banner-slide-enter-to,
.banner-slide-leave {
  opacity: 1;
  transform: translateX(0);
}

.xml-diff-banner {
  background-color: #fff5f5;
  border: none;
  border-left: 4px solid #dc3545;
  border-radius: 0 6px 6px 0;
  padding: 8px 12px;
  font-size: 0.85rem;
  color: #842029;
  margin-bottom: 10px;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.xml-diff-icon {
  margin-top: 2px;
  flex-shrink: 0;
}

.xml-diff-items {
  display: flex;
  flex-direction: column;
  flex: 1;
}

.xml-diff-separator {
  border: none;
  border-top: 1px solid #f5c2c7;
  margin: 6px 0;
}

.xml-diff-item {
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.form-label {
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 4px;
}

/* Aviso de nome obrigatório no formulário de cenário */
.name-required-hint {
  display: block;
  margin-top: 4px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #dc3545;
}

.form-field-text {
  font-size: 0.875rem;
  color: #495057;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 3px;
  padding: 3px 8px;
  min-height: 31px;
  display: flex;
  align-items: center;
  line-height: 1.4;
}


.xml-import-area {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.xml-badge-row {
  flex-basis: 100%;
  margin-top: 2px;
}

.action-btn {
  font-size: 0.75rem;
  padding: 2px 7px;
}

.toolbar .btn,
.editor-toolbar .btn {
  font-size: 0.75rem;
  padding: 2px 7px;
}

.scenario-manager .btn {
  transition: background-color 0.18s ease, border-color 0.18s ease, color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.scenario-manager .btn:not(:disabled):hover {
  transform: translate(-1px, -1px);
  box-shadow: 3px 3px 7px rgba(0, 0, 0, 0.28);
}

.scenario-manager .btn:not(:disabled):active {
  transform: translate(0, 0);
  box-shadow: none;
}


.scenario-manager .btn .b-icon,
.scenario-manager .btn .fa {
  font-size: 1em;
  vertical-align: -0.1em;
}

::v-deep .modal-footer .btn-primary {
  background-color: #ff5f00;
  border-color: #ff5f00;
}

::v-deep .modal-footer .btn-primary:not(:disabled):not(.disabled):active,
::v-deep .modal-footer .btn-primary:focus {
  background-color: #cc4c00;
  border-color: #cc4c00;
  box-shadow: none !important;
}

::v-deep #modalCreateFolder .form-control:focus,
::v-deep #modalCreateFolder .custom-select:focus,
::v-deep #modalRenameItem .form-control:focus {
  border-color: #ff5f00;
  box-shadow: 0 0 0 0.2rem rgba(255, 95, 0, 0.25);
}


.btn-pentagro-load {
  color: #00A39F !important;
  border-color: #00A39F !important;
  background: #fff !important;
  cursor: pointer;
}
.btn-pentagro-load:hover {
  background: #00A39F !important;
  color: #fff !important;
  border-color: #00A39F !important;
}


.btn-pentagro-save {
  color: #f37021 !important;
  border-color: #f37021 !important;
  background: #fff !important;
  cursor: pointer;
}
.btn-pentagro-save:hover:not(:disabled):not(.disabled) {
  background: #f37021 !important;
  color: #fff !important;
  border-color: #f37021 !important;
}

/* Estado desabilitado: deixa claro que nao da pra salvar */
.btn-pentagro-save:disabled,
.btn-pentagro-save.disabled {
  opacity: 0.5;
  cursor: not-allowed !important;
  pointer-events: none;
}
</style>

<style>
#modalScenarioManager .modal-body {
  overflow: hidden;
}

#modalScenarioManager .modal-dialog {
  max-height: 90vh;
}

#modalScenarioManager .modal-content {
  max-height: 85vh;
}

.tree-container .tree {
  min-width: max-content;
}

@keyframes nodeSlideIn {
  from { opacity: 0; transform: translateX(-10px); }
  to   { opacity: 1; transform: translateX(0); }
}

.tree-node {
  animation: nodeSlideIn 0.22s ease both;
}

.tree-node:nth-child(1)  { animation-delay: 0.00s; }
.tree-node:nth-child(2)  { animation-delay: 0.03s; }
.tree-node:nth-child(3)  { animation-delay: 0.06s; }
.tree-node:nth-child(4)  { animation-delay: 0.09s; }
.tree-node:nth-child(5)  { animation-delay: 0.12s; }
.tree-node:nth-child(6)  { animation-delay: 0.15s; }
.tree-node:nth-child(7)  { animation-delay: 0.18s; }
.tree-node:nth-child(8)  { animation-delay: 0.21s; }
.tree-node:nth-child(9)  { animation-delay: 0.24s; }
.tree-node:nth-child(10) { animation-delay: 0.27s; }
.tree-node:nth-child(11) { animation-delay: 0.30s; }
.tree-node:nth-child(12) { animation-delay: 0.33s; }
.tree-node:nth-child(13) { animation-delay: 0.36s; }
.tree-node:nth-child(14) { animation-delay: 0.39s; }
.tree-node:nth-child(n+15) { animation-delay: 0.42s; }

.tree-node > .tree-content {
  transition: background 0.2s ease;
}

.tree-node.selected > .tree-content {
  background: linear-gradient(90deg, #ff5f0018 0%, #ff5f0008 100%) !important;
  border-left: 3px solid #ff5f00;
}

.tree-node > .tree-content:hover {
  background-color: #ff5f0010;
}

</style>
